import { RelativePosition } from "@/api/dtos";
import { SaveSheetButton } from "@/components/form-items/SaveSheetButton";
import { HomeButton } from "@/components/navigation/HomeButton";
import {
  RelativePositionNav,
  sheetNavButtonClass,
} from "@/components/navigation/RelativePositionNav";
import { Button } from "@/components/ui/button.tsx";
import { Label } from "@/components/ui/label";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from "@/components/ui/menubar.tsx";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/auth/AuthContext";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { resolveDashboardListBackHref } from "@/lib/dashboardListScrollRestore";
import { ChevronDownIcon, EyeIcon, Trash2 } from "lucide-react";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

export interface FormHeaderProps {
  title?: string;
  backLabel: string;
  onSave: (status?: "published" | "draft" | "bin") => void;
  onAddToTrash: () => void;
  linkOverview?: string;
  addToTrashDisabled?: boolean;
  /** Historique des modifications (drawer) */
  sheetHistory?: ReactNode;
  /** Élément de menu "Dupliquer" injecté dans le dropdown Actions (optionnel). */
  duplicate?: ReactNode;
  print?: ReactNode;
  /** Élément de menu « Épingler à l'accueil » du dropdown Actions (optionnel). */
  pinToHome?: ReactNode;
  publicationStatus?: "published" | "draft" | "bin";
  relativePosition?: RelativePosition;
  /** When true, disables the "Save and publish" action (draft save remains available). */
  publishDisabled?: boolean;
}

/**
 * Réserve à droite pour le cluster Actions (absolute).
 * Sur grand écran la marge hors max-w-screen-2xl suffit → pr tombe à 2rem.
 * Sur écran ≤ fiche, on réserve ~26rem pour ne pas couper précédent/suivant.
 */
const ACTIONS_SAFE_PR =
  "pr-[max(2rem,calc(26rem-(100vw-96rem)/2))]";

const FormHeader = ({
  title,
  backLabel,
  onSave,
  onAddToTrash,
  linkOverview,
  addToTrashDisabled,
  sheetHistory,
  duplicate,
  print,
  pinToHome,
  publicationStatus,
  relativePosition,
  publishDisabled = false,
}: FormHeaderProps) => {
  const navigate = useNavigate();
  const pathname = window.location.pathname.split("/").slice(1);
  const type = pathname[1];

  const isArtworkOrMultipleprint = useMemo(() => {
    return type === "artwork" || type === "multipleprint";
  }, [type]);

  const isAuthorOrCollector = useMemo(() => {
    return type === "author" || type === "collector";
  }, [type]);

  const { user } = useAuth();
  const isReader = user?.role === "reader" ? true : false;

  const location = useLocation();

  const linkBack = resolveDashboardListBackHref(`/dashboard/${pathname[1]}`);

  const { t } = useTranslation();
  const vt = useVisibleT();

  const buildSiblingLink = (id: string) =>
    `/edit/${type}/${id}${location.search}`;

  return (
    <Menubar className="sticky top-0 z-50 flex h-[70px] w-full items-center justify-center space-x-0 overflow-visible rounded-none border-0 border-b border-slate-300 bg-white px-0 py-0 shadow-sm">
      {/* Même centrage que FormHeadingMetadata (max-w-screen-2xl mx-auto + px-8) */}
      <div
        className={`mx-auto flex h-full w-full max-w-screen-2xl items-center justify-between gap-2 pl-8 sm:gap-3 ${ACTIONS_SAFE_PR}`}
      >
        <div className="flex shrink-0 items-center gap-2">
          <HomeButton />
          <NavLink to={linkBack} className={`${sheetNavButtonClass} shrink-0`}>
            {backLabel}
          </NavLink>
        </div>

        <div className="flex min-w-0 flex-1 items-baseline justify-center gap-2 overflow-hidden px-1">
          <h1 className="min-w-0 truncate font-['Raleway'] text-lg font-semibold text-[#0C111D]">
            {title}
          </h1>
          {relativePosition && (
            <span className="shrink-0 text-xs font-semibold text-[hsla(222,41%,8%,0.5)]">
              {relativePosition.position}/{relativePosition.total}
            </span>
          )}
        </div>

        <RelativePositionNav
          layout="adjacent"
          relativePosition={relativePosition}
          buildLink={buildSiblingLink}
          resourceType={type}
          className="relative z-20 shrink-0"
        />
      </div>

      {/* Actions hors flux : ne décale pas le centrage de la zone fiche */}
      <div className="absolute right-6 top-0 z-10 flex h-full items-center gap-2 bg-white pl-3">
        {!isReader && (
          <MenubarMenu>
            <MenubarTrigger
              className="flex min-w-fit items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-input bg-white px-3 py-2 hover:bg-slate-100"
              style={{ maxWidth: "100%", width: "auto" }}
            >
              <p className="truncate font-['Raleway'] text-sm font-semibold">
                {t("actions")}
              </p>
              <ChevronDownIcon className="h-5 w-5" />
            </MenubarTrigger>
            <MenubarContent>
              {print}
              {sheetHistory}
              {duplicate}
              {pinToHome}
              <MenubarSeparator />
              <MenubarItem
                disabled={addToTrashDisabled}
                onClick={() => onAddToTrash()}
                className="gap-2 text-red-500"
              >
                <Trash2 className="h-4 w-4 shrink-0" aria-hidden />
                {t("delete")}
              </MenubarItem>
            </MenubarContent>
          </MenubarMenu>
        )}
        {isReader && print}

        {linkOverview && (
          <Button
            onClick={() => {
              if (linkOverview) {
                navigate(linkOverview);
              }
            }}
            variant={"outline"}
            className="rounded-lg"
          >
            <EyeIcon className="mr-1 h-5 w-5 text-[#64748B]" />
            <div className="flex items-center justify-center px-0.5">
              <div className="font-['Raleway'] text-sm font-semibold leading-normal text-slate-800">
                {t("overview")}
              </div>
            </div>
          </Button>
        )}

        {!isArtworkOrMultipleprint && !isAuthorOrCollector && (
          <div className="flex items-center gap-1">
            <Switch
              id="publicationStatus"
              checked={publicationStatus === "published"}
              onCheckedChange={() =>
                publicationStatus === "published"
                  ? onSave("draft")
                  : onSave("published")
              }
            />
            <Label
              htmlFor="publicationStatus"
              className="font-['Raleway'] text-sm font-medium leading-normal text-slate-700"
            >
              {t("publishSwitch")}
            </Label>
          </div>
        )}

        {!isReader && !isArtworkOrMultipleprint && (
          <Button
            variant="default"
            className="font-['Raleway'] text-sm font-semibold"
            onClick={() => onSave(publicationStatus)}
          >
            {vt("bo.form.save", "Enregistrer")}
          </Button>
        )}

        {!isReader && isArtworkOrMultipleprint && (
          <SaveSheetButton onSave={onSave} publishDisabled={publishDisabled} />
        )}
      </div>
    </Menubar>
  );
};

export default FormHeader;
